import { memo, useCallback } from 'react';
import { Box, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { QuantitySelector } from 'pages/sales/modules/components';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { SText } from '@setvi/shared/components';
import { FirstRowBox, RowContainer, SecondRowBox } from '../styled';
import { useFieldLock } from '../../../../pages/details/realtime/use-field-locks';
import { FieldLockIcon, FieldLockAvatar } from '../field-lock-badge';

interface QuantityColumnProps {
  product: QuoteProductDTO;
  productId: string;
  onQuantityChange: (productId: string, value: number) => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const useStyles = makeStyles(() => ({
  textFieldClassName: {
    '& input': {
      padding: '8px 12px'
    }
  },
  // "Another user is editing" state. The stepper is replaced by a read-only
  // pill (no +/- buttons to crowd the indicators): soft blue accent, lock glyph
  // at the start, value centered, editor avatar at the top-right corner.
  lockedPill: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 72,
    height: 40,
    padding: '0 14px 0 30px',
    borderRadius: 99,
    border: '1px solid #9DC7F2',
    backgroundColor: '#EEF4FF',
    color: '#5D7083',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'not-allowed'
  }
}));

export const QuantityColumn = memo(
  ({
    product,
    productId,
    onQuantityChange,
    disabled = false,
    disabledTooltip = ''
  }: QuantityColumnProps) => {
    const classes = useStyles();
    const { lockedByOther, holder, acquire, release } = useFieldLock(
      productId,
      'quantity'
    );

    const step =
      product?.minimumQuoteQuantity > 1 &&
      product?.overrideMinimumQuoteQuantity !== true
        ? product.minimumQuoteQuantity
        : 1;

    const handleQuantityChange = useCallback(
      (value: number) => {
        if (!disabled) {
          onQuantityChange(productId, value);
        }
      },
      [disabled, onQuantityChange, productId]
    );

    if (product?.isPricingFetching) {
      return (
        <RowContainer>
          <FirstRowBox>
            <Skeleton variant="rect" width={130} height={40} />
          </FirstRowBox>
          <SecondRowBox>
            {!!product.unitOfMeasure && (
              <Skeleton
                variant="rect"
                width={100}
                height={30}
                style={{ margin: '0 auto' }}
              />
            )}
          </SecondRowBox>
        </RowContainer>
      );
    }

    const quantityInput = lockedByOther ? (
      <Box className={classes.lockedPill}>
        <FieldLockIcon />
        {product?.quantity}
        <FieldLockAvatar holder={holder} />
      </Box>
    ) : (
      <Box
        maxWidth={160}
        onFocus={acquire}
        onBlur={release}
        style={{ position: 'relative' }}>
        <QuantitySelector
          quantity={product?.quantity}
          min={step}
          step={step}
          onInputChange={handleQuantityChange}
          textFieldClassName={classes.textFieldClassName}
          disabled={disabled}
        />
      </Box>
    );

    // When locked, the avatar carries the "X is editing" tooltip — don't wrap
    // the pill in a second tooltip. Only show the not-editable tooltip.
    const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : '';

    return (
      <RowContainer>
        <FirstRowBox>
          {tooltipTitle ? (
            <Tooltip title={tooltipTitle} arrow>
              <Box>{quantityInput}</Box>
            </Tooltip>
          ) : (
            quantityInput
          )}
        </FirstRowBox>
        <SecondRowBox>
          {!!product.unitOfMeasure && (
            <SText
              title={`UOM: ${product.unitOfMeasure}`}
              style={{
                width: '100%',
                textAlign: 'center'
              }}
            />
          )}
        </SecondRowBox>
      </RowContainer>
    );
  }
);

QuantityColumn.displayName = 'QuantityColumn';
